import { useEffect, useMemo, useRef, useState } from 'react';
import { getShiftStart } from '../../../../shared/services/shifts/shifts.ts';
import { useResizeObserver } from '../../../hooks/useResizeObserver.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { ReportSettings } from '../report-settings/reportSettings.ts';
import { ReportGraphContainer } from '../reportGraphContainer.tsx';
import { drawMeasurementShiftDelays } from './drawMeasurementShiftDelays.ts';
import { getShiftDelayDays } from './getShiftDelayDays.ts';
import { MeasurementShiftDelayDialog } from './measurementShiftDelayDialog.tsx';

interface Props {
    reportSettings: ReportSettings;
}

export function MeasurementShiftDelaysGraph({ reportSettings }: Props) {
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const dimensions = useResizeObserver(graphContainerRef);
    const [selectedShiftEnd, setSelectedShiftEnd] = useState<number | null>(null);
    const { data, isFetching } = trpc.graphsData.getMeasurementShiftDelays.useQuery({
        start: getShiftStart(reportSettings.first.start),// Get extra shift to have previous night shift data
        end: reportSettings.last.end,
    }, {
        refetchOnWindowFocus: false,
    });
    const visibleData = useMemo(() => {
        return (data ?? [])
            .filter((shift) => reportSettings.visibleShifts.includes(shift.shiftSlot))
            .filter((shift) => shift.isWeekend ? reportSettings.weekends : reportSettings.workdays);
    }, [data, reportSettings.visibleShifts, reportSettings.weekends, reportSettings.workdays]);
    const selectedShift = selectedShiftEnd == null
        ? null
        : visibleData.find((shift) => shift.shiftEnd.getTime() === selectedShiftEnd) ?? null;
    const dayKeys = useMemo(() => {
        return getShiftDelayDays(reportSettings.first.start, reportSettings.last.end);
    }, [reportSettings.first.start, reportSettings.last.end]);
    const visibleShiftCount = reportSettings.visibleShifts.length;
    const dayWidth = 50 + visibleShiftCount * 40;

    useEffect(() => {
        if (!graphContainerRef.current) return;
        drawMeasurementShiftDelays(
            graphContainerRef.current,
            visibleData,
            dayKeys,
            reportSettings.visibleShifts,
            (shiftEnd) => setSelectedShiftEnd(shiftEnd.getTime()),
        );
    }, [dimensions, visibleData, dayKeys, reportSettings.visibleShifts]);

    return (
        <>
            <ReportGraphContainer
                style={{ height: '100%', width: '100%', minWidth: Math.max(700, dayKeys.length * dayWidth) }}
                containerRef={graphContainerRef}
                isLoading={isFetching}
            />
            {selectedShift && (
                <MeasurementShiftDelayDialog
                    shiftDelay={selectedShift}
                    onClose={() => setSelectedShiftEnd(null)}
                />
            )}
        </>
    );
}
